<template>
  <div class="row py-6 pt-lg-7 pb-lg-8 gy-4">
    <div class="col-12 col-lg-8">
      <h1 class="h2 mb-2 mb-md-4">Winkelmandje</h1>

      <div v-if="cartItems.length === 0">
        <p>Je hebt nog geen pakketten toegevoegd aan je winkelmandje.</p>
        <div class="row g-2">
          <div class="col-auto">
            <a href="/samenstellen" class="btn btn--primary">
              Pakket samenstellen<i class="icon icon-package"></i>
            </a>
          </div>
        </div>
      </div>

      <div v-else class="row g-2">
        <div v-if="isRecalculating" class="col-12 mb-2">
          <div class="d-flex align-items-center gap-2 text-muted fs-sm">
            <div
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></div>
            <span>Prijzen worden bijgewerkt...</span>
          </div>
        </div>

        <div
          v-for="(item, index) in cartItemsWithPrices"
          :key="`${index}-${item.personalization?.selectedCard?.price || 0}-${item.quantity}`"
          class="col-12"
        >
          <div
            class="cart__item"
            :class="{
              expanded: expandedItems[index],
              invalid: !item.packageName || !item.pricePerUnit,
            }"
          >
            <div class="cart__item-header">
              <div class="cart__item-header-title">
                <div class="cart__item-chevron order-2 order-lg-0">
                  <button
                    @click="toggleCollapse(index)"
                    class="btn btn--icon-sm btn--transparent p-0"
                    :aria-expanded="expandedItems[index] ? 'true' : 'false'"
                  >
                    <i
                      class="icon icon-chevron"
                      :class="{ rotated: expandedItems[index] }"
                    ></i>
                  </button>
                </div>
                <div v-if="getCartItemImage(item)" class="cart__item-image">
                  <figure class="rimg rimg--100-100 rimg--contain">
                    <img
                      :src="getCartItemImage(item).src"
                      :alt="getCartItemImage(item).alt"
                    />
                  </figure>
                </div>
                <div class="cart__item-title flex-grow-1 flex-grow-lg">
                  <span class="d-block h6 mb-0">
                    {{ decodeHtml(item.packageName) }}
                  </span>
                  <span
                    v-if="!item.packageName || !item.pricePerUnit"
                    class="d-flex gap-05 align-items-center mt-05 fs-xs text-primary fw-semi-bold"
                  >
                    <i class="icon icon-alert"></i>
                    Minimale orderwaarde niet behaald
                  </span>
                </div>
              </div>
              <div class="cart__item-header-details justify-content-end">
                <div class="cart__item-price">
                  <span class="d-block text-black fs-sm">{{
                    formatPrice(item.pricePerUnit, priceFormat)
                  }}</span>
                  <span class="d-block text-gray-500 fs-xs">/ pakket</span>
                </div>
                <div class="cart__item-quantity">
                  <div
                    class="d-flex gap-2 justify-content-between align-items-center"
                  >
                    <QuantityInput
                      :value="item.quantity || 10"
                      :min="10"
                      :max="99999"
                      :step="1"
                      size="sm"
                      :aria-label="'Aantal pakketten'"
                      @update:value="
                        (newQuantity) =>
                          updatePackageQuantity(index, newQuantity)
                      "
                      @quantity-changed="handleQuantityChanged"
                      @invalid="handleQuantityInvalid"
                      @corrected="handleQuantityCorrected"
                    />
                    <button
                      @click="removeItem(index)"
                      class="remove"
                      title="Pakket verwijderen"
                    >
                      <i class="icon icon-trashbin"></i>
                    </button>
                  </div>
                </div>
                <div class="cart__item-subtotal">
                  <span class="cart__subtotal-label">Totaal:</span>
                  <span class="cart__subtotal">{{
                    formatPrice(item.pricePerUnit * item.quantity, priceFormat)
                  }}</span>
                </div>
              </div>
            </div>

            <transition name="collapse">
              <div v-if="expandedItems[index]" class="cart__item-details">
                <!-- Products -->
                <div class="mb-3">
                  <button
                    @click="navigateToConfiguratorStep(index, 'products')"
                    class="btn btn--transparent-sm btn--sm ps-0"
                  >
                    Producten<i class="icon icon-arrow"></i>
                  </button>

                  <!-- Products items -->
                  <div
                    v-if="item.products && item.products.length > 0"
                    v-for="(product, productIndex) in item.products"
                    :key="productIndex"
                    class="cart__product"
                  >
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <figure
                        v-if="getProductImage(product)"
                        @click="
                          openProductModal(
                            product,
                            index,
                            productIndex,
                            'products',
                          )
                        "
                        class="img clickable"
                      >
                        <img
                          :src="getProductImage(product).src"
                          :alt="getProductImage(product).alt"
                        />
                      </figure>
                      <div
                        class="d-flex flex-column clickable"
                        @click="
                          openProductModal(
                            product,
                            index,
                            productIndex,
                            'products',
                          )
                        "
                      >
                        <span class="title">{{
                          decodeHtml(product.name)
                        }}</span>
                        <div
                          v-if="getProductPersonalization(product, item)"
                          class="subtitle"
                        >
                          Drukwerk:
                          {{ getProductPersonalization(product, item) }}
                        </div>
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            decreaseQuantity(index, productIndex, 'products')
                          "
                          class="btn btn--icon-sm btn--gray"
                          :disabled="(product.quantity || 1) <= 1"
                        >
                          <i class="icon icon-min"></i>
                        </button>
                        <span class="fs-sm quantity">{{
                          product.quantity || 1
                        }}</span>
                        <button
                          @click="
                            increaseQuantity(index, productIndex, 'products')
                          "
                          class="btn btn--icon-sm btn--gray"
                        >
                          <i class="icon icon-plus"></i>
                        </button>
                      </div>
                      <div class="cart__product-remove">
                        <button
                          @click="
                            removeFromPackage(index, productIndex, 'products')
                          "
                          class="remove"
                          title="Product verwijderen"
                        >
                          <i class="icon icon-trashbin"></i>
                        </button>
                      </div>
                      <div class="cart__product-price">
                        <span class="fs-sm">{{
                          formatPrice(
                            (product.price || 0) * (product.quantity || 1),
                            priceFormat,
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Products empty -->
                  <div v-else class="cart__product">
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image"></div>
                      <div class="d-flex flex-column">
                        <span class="title text-muted fw-light"
                          >Nog geen producten gekozen</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="navigateToConfiguratorStep(index, 'products')"
                          class="btn btn--gray btn--sm"
                        >
                          Kies producten
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- foodDrinks -->
                <div class="mb-3">
                  <button
                    @click="navigateToConfiguratorStep(index, 'foodDrinks')"
                    class="btn btn--transparent-sm btn--sm ps-0"
                  >
                    Eten & drinken<i class="icon icon-arrow"></i>
                  </button>

                  <!-- foodDrinks items -->
                  <div
                    v-if="item.foodDrinks && item.foodDrinks.length > 0"
                    v-for="(foodDrink, foodIndex) in item.foodDrinks"
                    :key="foodIndex"
                    class="cart__product"
                  >
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <figure
                        v-if="getProductImage(foodDrink)"
                        @click="
                          openProductModal(
                            foodDrink,
                            index,
                            foodIndex,
                            'foodDrinks',
                          )
                        "
                        class="img clickable"
                      >
                        <img
                          :src="getProductImage(foodDrink).src"
                          :alt="getProductImage(foodDrink).alt"
                        />
                      </figure>
                      <div
                        class="d-flex flex-column clickable"
                        @click="
                          openProductModal(
                            foodDrink,
                            index,
                            foodIndex,
                            'foodDrinks',
                          )
                        "
                      >
                        <span class="title">{{
                          decodeHtml(foodDrink.name)
                        }}</span>
                        <div
                          v-if="getProductPersonalization(foodDrink, item)"
                          class="subtitle"
                        >
                          Drukwerk:
                          {{ getProductPersonalization(foodDrink, item) }}
                        </div>
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            decreaseQuantity(index, foodIndex, 'foodDrinks')
                          "
                          class="btn btn--icon-sm btn--gray"
                          :disabled="(foodDrink.quantity || 1) <= 1"
                        >
                          <i class="icon icon-min"></i>
                        </button>
                        <span class="fs-sm quantity">{{
                          foodDrink.quantity || 1
                        }}</span>
                        <button
                          @click="
                            increaseQuantity(index, foodIndex, 'foodDrinks')
                          "
                          class="btn btn--icon-sm btn--gray"
                        >
                          <i class="icon icon-plus"></i>
                        </button>
                      </div>
                      <div class="cart__product-remove">
                        <button
                          @click="
                            removeFromPackage(index, foodIndex, 'foodDrinks')
                          "
                          class="remove"
                          title="Product verwijderen"
                        >
                          <i class="icon icon-trashbin"></i>
                        </button>
                      </div>
                      <div class="cart__product-price">
                        <span class="fs-sm">{{
                          formatPrice(
                            (foodDrink.price || 0) * (foodDrink.quantity || 1),
                            priceFormat,
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- foodDrinks empty -->
                  <div v-else class="cart__product">
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image"></div>
                      <div class="d-flex flex-column">
                        <span class="title text-muted fw-light"
                          >Nog geen eten & drinken gekozen</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'foodDrinks')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Kies eten & drinken
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Packaging -->
                <div class="mb-3">
                  <button
                    @click="navigateToConfiguratorStep(index, 'packaging')"
                    class="btn btn--transparent-sm btn--sm ps-0"
                  >
                    Verpakking<i class="icon icon-arrow"></i>
                  </button>

                  <!-- Packaging items -->
                  <div
                    v-if="
                      (item.packagingMode === 'product' &&
                        item.packagingProduct) ||
                      (item.packagingMode === 'box' &&
                        item.packaging &&
                        item.packaging.id !== 'default-packaging')
                    "
                    class="cart__product"
                  >
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <figure
                        v-if="getProductImage(item.packagingProduct)"
                        @click="
                          openProductModal(
                            item.packagingProduct,
                            index,
                            null,
                            'packaging',
                          )
                        "
                        class="img clickable"
                      >
                        <img
                          :src="getProductImage(item.packagingProduct).src"
                          :alt="getProductImage(item.packagingProduct).alt"
                        />
                      </figure>
                      <figure
                        v-else-if="getProductImage(item.packaging)"
                        @click="
                          openProductModal(
                            item.packaging,
                            index,
                            null,
                            'packaging',
                          )
                        "
                        class="img clickable"
                      >
                        <img
                          :src="getProductImage(item.packaging).src"
                          :alt="getProductImage(item.packaging).alt"
                        />
                      </figure>
                      <div
                        class="d-flex flex-column clickable"
                        @click="
                          openProductModal(
                            item.packagingProduct || item.packaging,
                            index,
                            null,
                            'packaging',
                          )
                        "
                      >
                        <span class="title" v-if="item.packagingProduct">{{
                          item.packagingProduct.name
                        }}</span>
                        <span class="title" v-else-if="item.packaging">{{
                          item.packaging.name
                        }}</span>
                        <!-- Subtitle -->
                        <div
                          v-if="
                            (item.packagingProduct &&
                              getProductPersonalization(
                                item.packagingProduct,
                                item,
                              )) ||
                            (item.packaging &&
                              getProductPersonalization(item.packaging, item))
                          "
                          class="subtitle"
                        >
                          Drukwerk:
                          {{
                            item.packagingProduct
                              ? getProductPersonalization(
                                  item.packagingProduct,
                                  item,
                                )
                              : getProductPersonalization(item.packaging, item)
                          }}
                        </div>
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'packaging')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Wijzig
                        </button>
                      </div>
                      <div class="cart__product-remove">
                        <button
                          @click="removeFromPackage(index, null, 'packaging')"
                          class="remove"
                          title="Verpakking verwijderen"
                        >
                          <i class="icon icon-trashbin"></i>
                        </button>
                      </div>
                      <div class="cart__product-price">
                        <span v-if="item.packagingProduct" class="fs-sm">
                          {{
                            formatPrice(
                              item.packagingProduct.price || 0,
                              priceFormat,
                            )
                          }}
                        </span>
                        <span v-else-if="item.packaging" class="fs-sm">
                          {{
                            formatPrice(item.packaging.price || 0, priceFormat)
                          }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Packaging empty -->
                  <div v-else class="cart__product">
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image"></div>
                      <div class="d-flex flex-column">
                        <span class="title text-muted fw-light"
                          >Geen verpakking gekozen</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'packaging')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Kies verpakking
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Personalization  -->
                <div class="mb-3">
                  <button
                    @click="
                      navigateToConfiguratorStep(index, 'personalization')
                    "
                    class="btn btn--transparent-sm btn--sm ps-0 pb-0"
                  >
                    Personalisatie<i class="icon icon-arrow"></i>
                  </button>

                  <!-- Printing products -->
                  <div
                    v-if="item.personalizedProductsCount > 0"
                    class="cart__product"
                  >
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image">
                        <figure class="img"></figure>
                      </div>
                      <div class="d-flex flex-column">
                        <span class="title"
                          >Bedrukken van
                          {{ item.personalizedProductsCount }} product{{
                            item.personalizedProductsCount !== 1 ? 'en' : ''
                          }}</span
                        >
                        <span v-if="getLogoFilename(item)" class="subtitle"
                          >Logo: {{ getLogoFilename(item) }}</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'personalization')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Wijzig
                        </button>
                      </div>
                      <div class="cart__product-remove">
                        <button
                          @click="
                            removeFromPackage(index, null, 'personalization')
                          "
                          class="remove"
                          title="Personalisatie verwijderen"
                        >
                          <i class="icon icon-trashbin"></i>
                        </button>
                      </div>
                      <div class="cart__product-price">
                        <span class="fs-sm">
                          {{
                            item.personalization?.printing?.total_ex_vat !==
                            undefined
                              ? item.personalization.printing.total_ex_vat > 0
                                ? formatPrice(
                                    item.personalization.printing.total_ex_vat /
                                      (item.quantity || 1),
                                    priceFormat,
                                  )
                                : 'Gratis bedrukking'
                              : item.personalizedProductsCount > 0
                                ? 'Berekenen...'
                                : 'Geen bedrukking'
                          }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Printing products empty -->
                  <div v-else class="cart__product">
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image"></div>
                      <div class="d-flex flex-column">
                        <span class="title text-muted fw-light"
                          >Nog geen logo bedrukking gekozen</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'personalization')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Producten bedrukken
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Selected Card -->
                  <div
                    v-if="item.personalization?.selectedCard"
                    class="cart__product"
                  >
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <figure
                        v-if="
                          getProductImage(item.personalization.selectedCard)
                        "
                        @click="
                          openProductModal(
                            item.personalization.selectedCard,
                            index,
                            null,
                            'card',
                          )
                        "
                        class="img clickable"
                      >
                        <img
                          :src="
                            getProductImage(item.personalization.selectedCard)
                              .src
                          "
                          :alt="
                            getProductImage(item.personalization.selectedCard)
                              .alt
                          "
                        />
                      </figure>
                      <div
                        class="d-flex flex-column clickable"
                        @click="
                          openProductModal(
                            item.personalization.selectedCard,
                            index,
                            null,
                            'card',
                          )
                        "
                      >
                        <span class="title">{{
                          decodeHtml(item.personalization.selectedCard.name)
                        }}</span>
                        <span class="subtitle"
                          >Wij nemen contact op voor personalisatie</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'personalization')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Wijzig
                        </button>
                      </div>
                      <div class="cart__product-remove">
                        <button
                          @click="removeFromPackage(index, null, 'card')"
                          class="remove"
                          title="Personalisatie verwijderen"
                        >
                          <i class="icon icon-trashbin"></i>
                        </button>
                      </div>
                      <div class="cart__product-price">
                        <span class="fs-sm">{{
                          formatPrice(
                            item.personalization.selectedCard.price,
                            priceFormat,
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Selected Card empty -->
                  <div v-else class="cart__product">
                    <!-- Product header -->
                    <div class="cart__product-header">
                      <div class="cart__product-image"></div>
                      <div class="d-flex flex-column">
                        <span class="title text-muted fw-light"
                          >Nog geen kaart gekozen</span
                        >
                      </div>
                    </div>

                    <!-- Product details -->
                    <div class="cart__product-details">
                      <div class="cart__product-quantity">
                        <button
                          @click="
                            navigateToConfiguratorStep(index, 'personalization')
                          "
                          class="btn btn--gray btn--sm"
                        >
                          Kaart kiezen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Personalization empty -->
                <div
                  v-if="
                    !item.products?.length &&
                    !item.foodDrinks?.length &&
                    !item.packaging &&
                    personalizedProductsCount === 0
                  "
                  class="text-muted mb-2"
                >
                  <em>Geen details beschikbaar voor dit pakket.</em>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div v-if="cartItems.length > 0" class="col-12 col-lg-4">
      <div class="summary fs-sm">
        <span class="d-block h4">Jouw bestelling</span>

        <div class="row flex-column gy-2">
          <div class="col">
            <div
              class="row align-items-center justify-content-between text-black"
            >
              <div class="col-auto">
                <span>Subtotaal ex. BTW</span>
              </div>
              <div class="col-auto text-end">
                <span>{{ formatPrice(subtotalExVat, priceFormat) }}</span>
              </div>
            </div>
          </div>
          <!-- Drukwerk kosten zijn al inbegrepen in pakketprijs -->
          <div v-if="volumeDiscountPercent > 0" class="col">
            <div
              class="row align-items-center justify-content-between text-black"
            >
              <div class="col-auto">
                <span>Staffelkorting ({{ volumeDiscountPercent }}%)</span>
              </div>
              <div class="col-auto text-end">
                <span
                  >-{{ formatPrice(volumeDiscountAmount, priceFormat) }}</span
                >
              </div>
            </div>
          </div>
          <div v-for="item in vatRatesArray" :key="item.rate" class="col">
            <div
              class="row align-items-center justify-content-between text-black"
            >
              <div class="col-auto">
                <span
                  >BTW-{{ getVatRateLabel(item.rate) }} ({{
                    (item.rate * 100).toFixed(0)
                  }}%)</span
                >
              </div>
              <div class="col-auto text-end">
                <span>{{ formatPrice(item.amount, priceFormat) }}</span>
              </div>
            </div>
          </div>
          <div class="col">
            <div
              class="row align-items-center justify-content-between text-black fw-bold"
            >
              <div class="col-auto">
                <span>Totaal incl. BTW</span>
              </div>
              <div class="col-auto text-end">
                <span>{{
                  formatPrice(vatBreakdown.totalInclVat, priceFormat)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-2 mt-1">
          <div class="col-12 col-md">
            <button
              type="button"
              @click="proceedToCheckout"
              class="btn btn--cta w-100"
            >
              Naar gegevens<i class="icon icon-checkmark"></i>
            </button>
          </div>

          <div class="col-12 col-md">
            <button
              ref="saveCartBtn"
              type="button"
              @click="saveCartManually"
              class="btn btn--gray w-100 saved-cart-btn"
              :class="{ 'saved-cart-btn--locked': !isUserLoggedIn }"
              :disabled="isSaving"
              v-bind="
                !isUserLoggedIn
                  ? {
                      'data-bs-toggle': 'tooltip',
                      'data-bs-title': 'Log in om je winkelmandje op te slaan',
                      'data-bs-placement': 'top',
                      'data-bs-trigger': 'hover',
                    }
                  : {}
              "
            >
              Bewaar winkelmandje
              <i
                class="icon"
                :class="cartSavedRecently ? 'icon-heart-filled' : 'icon-heart'"
              ></i>
            </button>
            <span
              v-if="showSavedNotice"
              class="d-block text-gray-700 text-center fs-xs mt-2"
            >
              Winkelmandje opgeslagen
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Detail modal -->
  <ProductDetailModal
    v-if="showProductDetailModal"
    :product="detailModalDetails.product"
    :count="detailModalDetails.product.count"
    :isPersonalizedCard="detailModalDetails.type === 'card'"
    :selected="true"
    :isPackaging="detailModalDetails.type === 'packaging'"
    @close-modal="showProductDetailModal = false"
    @decrease="
      decreaseQuantity(
        detailModalDetails.index,
        detailModalDetails.productIndex,
        detailModalDetails.type,
      )
    "
    @increase="
      increaseQuantity(
        detailModalDetails.index,
        detailModalDetails.productIndex,
        detailModalDetails.type,
      )
    "
    @remove="
      removeFromPackage(
        detailModalDetails.index,
        detailModalDetails.productIndex,
        detailModalDetails.type,
      )
    "
    @select="
      removeFromPackage(detailModalDetails.index, null, detailModalDetails.type)
    "
  />
</template>

<script>
import { Tooltip } from 'bootstrap';
import QuantityInput from './configurator/components/QuantityInput.vue';
import {
  calculateVatBreakdownByProduct,
  formatPrice,
  getPriceFormat,
  getVolumeDiscountPercent,
  getHardcodedVolumeDiscount,
} from './configurator/utils/priceUtils.js';
import {
  decreaseProductCount,
  removePersonalization as removePersonalizationUtil,
  decodeHtml,
} from './configurator/utils/productCountUtils.js';
import ProductDetailModal from './configurator/modals/ProductDetailModal.vue';
import {
  imageUtils,
  htmlUtils,
  validationUtils,
  errorUtils,
} from './configurator/utils/componentUtils.js';

export default {
  name: 'Cart',
  components: {
    ProductDetailModal,
    QuantityInput,
  },

  data() {
    return {
      cartItems: [],
      priceFormat: getPriceFormat(),
      expandedItems: {},
      storedVolumeDiscountPercent: 0,
      showProductDetailModal: false,
      detailModalDetails: {
        product: {},
        index: null,
        productIndex: null,
        type: '',
      },
      isRecalculating: false,
      isSaving: false,
      isUserLoggedIn: false,
      saveCartTooltip: null,
      cartSavedRecently: false,
      showSavedNotice: false,
      vatBreakdownData: {
        subtotalExVat: 0,
        subtotalAfterDiscount: 0,
        volumeDiscountPercent: 0,
        volumeDiscountAmount: 0,
        vatRates: new Map(),
        total: 0,
        totalInclVat: 0,
        totalVat: 0,
      },
    };
  },

  async mounted() {
    await this.loadCart();
    this.checkUserLoggedIn();
    this.initializeSaveCartTooltip();
    this.$nextTick(() => {
      this.recalculateAllCartItems().catch(() => {});
    });
  },

  beforeUnmount() {
    this.destroySaveCartTooltip();
  },

  watch: {
    isUserLoggedIn() {
      this.reinitializeSaveCartTooltip();
    },
    cartItemsWithPrices: {
      handler() {
        this.updateVatBreakdown();
      },
      deep: true,
      immediate: false,
    },
    storedVolumeDiscountPercent() {
      this.updateVatBreakdown();
    },
  },

  computed: {
    subtotalExVat() {
      return this.vatBreakdown.subtotalExVat;
    },
    originalSubtotalExVat() {
      return (
        this.vatBreakdown.originalSubtotalExVat ||
        this.vatBreakdown.subtotalExVat
      );
    },

    subtotalAfterDiscount() {
      return this.vatBreakdown.subtotalAfterDiscount;
    },

    volumeDiscountPercent() {
      return this.vatBreakdown.volumeDiscountPercent;
    },

    volumeDiscountAmount() {
      return this.vatBreakdown.volumeDiscountAmount;
    },

    cartItemsWithPrices() {
      return this.cartItems.map((item, itemIndex) => {
        const filteredProducts = (item.products || []).filter((product) => {
          if (
            (item.packaging && item.packaging.id === product.id) ||
            (item.packagingProduct && item.packagingProduct.id === product.id)
          ) {
            return false;
          }
          return true;
        });

        return {
          ...item,
          pricePerUnit: this.calculatePricePerUnit(item),
          lineTotalExVat:
            this.calculatePricePerUnit(item) * (item.quantity || 1),
          personalizedProductsCount: this.getPersonalizedProductsCount(item),
          products: filteredProducts,
        };
      });
    },

    vatBreakdown() {
      return this.vatBreakdownData;
    },

    vatRatesArray() {
      const rates = [];
      if (this.vatBreakdown.vatRates) {
        this.vatBreakdown.vatRates.forEach((amount, rate) => {
          rates.push({ rate, amount });
        });
      }
      return rates;
    },

    cartItemsHash() {
      return this.cartItemsWithPrices.map((item) => ({
        id: item.packageName,
        quantity: item.quantity,
        pricePerUnit: item.pricePerUnit,
        printingTotal: item.personalization?.printing?.total_ex_vat,
        products: item.products?.length || 0,
        foodDrinks: item.foodDrinks?.length || 0,
      }));
    },

    total() {
      return this.vatBreakdown.total;
    },
    printingCosts() {
      const allPrintingCosts = [];

      this.cartItemsWithPrices.forEach((item) => {
        if (item.personalization?.printing?.breakdown) {
          const breakdown = item.personalization.printing.breakdown;

          if (breakdown.sticker && breakdown.sticker.total > 0) {
            allPrintingCosts.push({
              method: 'Sticker',
              qty: breakdown.sticker.qty,
              unit: breakdown.sticker.unit,
              total: breakdown.sticker.total,
            });
          }

          if (breakdown.zeefdruk && breakdown.zeefdruk.total > 0) {
            allPrintingCosts.push({
              method: 'Zeefdruk',
              qty: breakdown.zeefdruk.qty,
              unit: breakdown.zeefdruk.unit,
              total: breakdown.zeefdruk.total,
            });
          }

          if (breakdown.graveren && breakdown.graveren.total > 0) {
            allPrintingCosts.push({
              method: 'Lasergravure',
              qty: breakdown.graveren.qty,
              unit: breakdown.graveren.unit,
              total: breakdown.graveren.total,
            });
          }
        }
      });

      return allPrintingCosts;
    },
    totalPrintingCosts() {
      return this.cartItemsWithPrices.reduce((total, item) => {
        if (item.personalization?.printing?.total_ex_vat) {
          return total + item.personalization.printing.total_ex_vat;
        }
        return total;
      }, 0);
    },

    hasIncompletePrintingData() {
      return this.cartItems.some((item) => {
        const personalizedProductsCount =
          this.getPersonalizedProductsCount(item);
        return personalizedProductsCount > 0 && !item.personalization?.printing;
      });
    },
  },

  watch: {
    cartItems: {
      handler: async function (newItems, oldItems) {
        if (newItems.length > 0) {
          await this.updateVolumeDiscount();
        } else {
          this.storedVolumeDiscountPercent = 0;
        }
      },
      deep: true,
    },

    hasIncompletePrintingData: {
      handler: async function (hasIncomplete) {
        if (hasIncomplete) {
          await this.recalculateAllCartItems();
        }
      },
      immediate: true,
    },
  },

  methods: {
    formatPrice,
    getHardcodedVolumeDiscount,
    decodeHtml,

    getCartItemImage(cartItem) {
      return imageUtils.getCartItemImage(cartItem);
    },

    getProductImage(product) {
      return imageUtils.getProductImage(product);
    },

    toggleCollapse(index) {
      this.expandedItems[index] = !this.expandedItems[index];
    },

    async updatePackagePrice(packageIndex) {
      const item = this.cartItems[packageIndex];
      const personalizedProductsCount = this.getPersonalizedProductsCount(item);
      if (!item) return;

      if (
        personalizedProductsCount > 0 &&
        !item.personalization?.printing?.total_ex_vat
      ) {
        try {
          await this.recalculatePrintingCosts(item);
        } catch (error) {
          console.error(
            'Error recalculating printing costs during package price update:',
            error,
          );
        }
      }
    },

    async recalculatePrintingCosts(item) {
      try {
        const { calculatePrintingCounts, calculatePrintingCosts } =
          await import('./configurator/utils/priceUtils.js');

        const allProducts = (item.products || [])
          .concat(item.foodDrinks || [])
          .concat([item.packagingProduct] || []);

        const printingCounts = calculatePrintingCounts(
          allProducts,
          item.quantity || 1,
          item.personalization?.productPersonalization || {},
        );

        const printingBreakdown = await calculatePrintingCosts(printingCounts);

        if (!item.personalization) {
          item.personalization = {};
        }

        item.personalization.printing = {
          counts: printingCounts,
          breakdown: printingBreakdown,
          total_ex_vat: printingBreakdown.totalExVat,
        };
      } catch (error) {
        console.error('Error recalculating printing costs:', error);
      }
    },

    async recalculateAllCartItems() {
      const itemsToRecalculate = this.cartItems.filter((item) => {
        const personalizedProductsCount =
          this.getPersonalizedProductsCount(item);
        return (
          personalizedProductsCount > 0 &&
          !item.personalization?.printing?.total_ex_vat
        );
      });

      if (itemsToRecalculate.length === 0) {
        return;
      }

      this.isRecalculating = true;

      try {
        const recalculationPromises = itemsToRecalculate.map(async (item) => {
          try {
            await this.recalculatePrintingCosts(item);
            item.pricePerUnit = this.calculatePricePerUnit(item);
          } catch (error) {
            console.error(
              'Error recalculating printing costs for cart item:',
              error,
            );
          }
        });

        await Promise.all(recalculationPromises);
      } finally {
        this.isRecalculating = false;
      }
    },

    async increaseQuantity(packageIndex, productIndex, type) {
      const item = this.cartItems[packageIndex];
      if (item[type] && item[type][productIndex]) {
        const product = item[type][productIndex];
        const currentQuantity = parseInt(product.quantity) || 1;
        const newQuantity = currentQuantity + 1;

        product.quantity = newQuantity;
        product.count = newQuantity;

        await this.updatePackagePrice(packageIndex);
        this.saveCart();
      }
    },

    async decreaseQuantity(packageIndex, productIndex, type) {
      const item = this.cartItems[packageIndex];
      if (item[type] && item[type][productIndex]) {
        const product = item[type][productIndex];
        const currentQuantity = parseInt(product.quantity) || 1;
        if (currentQuantity > 1) {
          const newQuantity = currentQuantity - 1;

          product.quantity = newQuantity;
          product.count = newQuantity;

          await this.updatePackagePrice(packageIndex);
          this.saveCart();
        } else {
          await this.removeFromPackage(packageIndex, productIndex, type);
        }
      }
    },

    async removeFromPackage(index, productIndex, type) {
      const item = this.cartItems[index];
      if (!item) {
        console.error('Invalid package index:', index);
        return;
      }

      if (type === 'packaging') {
        if (confirm('Weet je zeker dat je de verpakking wilt verwijderen?')) {
          item.packaging = null;
          item.packagingMode = 'box';
          item.packagingProduct = null;
          await this.updatePackagePrice(index);
          this.saveCart();
        }
      } else if (type === 'personalization') {
        if (
          confirm('Weet je zeker dat je alle personalisaties wilt verwijderen?')
        ) {
          if (item.personalization) {
            item.personalization.productPersonalization = {};
          }
          await this.updatePackagePrice(index);
          this.saveCart();
        }
      } else if (type === 'packaging') {
        if (confirm('Weet je zeker dat je de verpakking wilt verwijderen?')) {
          item.packaging = null;
          item.packagingMode = 'box';
          item.packagingProduct = null;
          await this.updatePackagePrice(index);
          this.saveCart();
        }
      }

      // Remove card
      else if (type === 'card') {
        if (
          confirm('Weet je zeker dat je de personalisatie wilt verwijderen?')
        ) {
          if (item.personalization) {
            item.personalization.selectedCard = null;
          }
          await this.updatePackagePrice(index);
          this.saveCart();
        }
      }

      // Remove product from package
      else if (type === 'products' || type === 'foodDrinks') {
        if (item[type] && item[type][productIndex]) {
          const productId = item[type][productIndex].id;
          const productName = item[type][productIndex].name;
          if (
            confirm(
              `Weet je zeker dat je "${productName}" wilt verwijderen uit dit pakket?`,
            )
          ) {
            // Remove from products
            item[type].splice(productIndex, 1);

            // Remove from personalisation
            item.personalization.productPersonalization =
              removePersonalizationUtil(
                item.personalization.productPersonalization,
                productId,
              );

            // Update & save
            await this.updatePackagePrice(index);
            this.saveCart();
          }
        }
      }

      this.showProductDetailModal = false;
    },

    async updateVolumeDiscount() {
      if (this.cartItemsWithPrices.length === 0) return;

      const totalQuantity = this.cartItemsWithPrices.reduce(
        (sum, item) => sum + (item.quantity || 1),
        0,
      );

      this.storedVolumeDiscountPercent =
        this.getHardcodedVolumeDiscount(totalQuantity);

      try {
        const volumeDiscountPercent =
          await getVolumeDiscountPercent(totalQuantity);
        if (volumeDiscountPercent !== this.storedVolumeDiscountPercent) {
          this.storedVolumeDiscountPercent = volumeDiscountPercent;
        }
      } catch (error) {
        console.warn(
          'Volume discount API failed, using hardcoded value:',
          error,
        );
      }
    },

    async updateVatBreakdown() {
      if (this.cartItemsWithPrices.length === 0) {
        this.vatBreakdownData = {
          subtotalExVat: 0,
          subtotalAfterDiscount: 0,
          volumeDiscountPercent: 0,
          volumeDiscountAmount: 0,
          vatRates: new Map(),
          total: 0,
          totalInclVat: 0,
          totalVat: 0,
        };
        return;
      }

      const totalQuantity = this.cartItems.reduce(
        (sum, item) => sum + (item.quantity || 1),
        0,
      );
      const volumeDiscountPercent =
        this.storedVolumeDiscountPercent ||
        this.getHardcodedVolumeDiscount(totalQuantity);

      try {
        const { prepareItemsWithCardPrices } = await import(
          './configurator/utils/priceUtils.js'
        );

        const itemsForCalculation = this.cartItems.map((item) => ({
          ...item,
          products: (item.products || []).filter((product) => {
            if (
              (item.packaging && item.packaging.id === product.id) ||
              (item.packagingProduct && item.packagingProduct.id === product.id)
            ) {
              return false;
            }
            return true;
          }),
        }));

        const preparedItems =
          await prepareItemsWithCardPrices(itemsForCalculation);

        const updatedCartItems = this.cartItems.map((item, index) => {
          const preparedItem = preparedItems[index];
          if (
            preparedItem?.personalization?.selectedCard &&
            item?.personalization?.selectedCard
          ) {
            const preparedCard = preparedItem.personalization.selectedCard;
            const currentCard = item.personalization.selectedCard;

            if (
              preparedCard.price !== currentCard.price ||
              preparedCard.tierPrice !== currentCard.tierPrice
            ) {
              return {
                ...item,
                personalization: {
                  ...item.personalization,
                  selectedCard: {
                    ...currentCard,
                    price: preparedCard.price,
                    originalPrice: preparedCard.originalPrice,
                    tierPrice: preparedCard.tierPrice,
                  },
                },
              };
            }
          }
          return item;
        });

        const hasChanges = updatedCartItems.some((item, index) => {
          const original = this.cartItems[index];
          if (!original) return false;
          const originalCard = original.personalization?.selectedCard;
          const updatedCard = item.personalization?.selectedCard;
          return (
            originalCard?.price !== updatedCard?.price ||
            originalCard?.tierPrice !== updatedCard?.tierPrice
          );
        });

        if (hasChanges) {
          this.cartItems = updatedCartItems;
        }

        const result = calculateVatBreakdownByProduct(
          preparedItems,
          volumeDiscountPercent,
          0,
        );
        this.vatBreakdownData = result;
      } catch {
        const result = calculateVatBreakdownByProduct(
          this.cartItemsWithPrices,
          volumeDiscountPercent,
          0,
        );
        this.vatBreakdownData = result;
      }
    },

    async loadCart() {
      try {
        const storedCartData = localStorage.getItem('composedPackages');
        if (storedCartData) {
          const parsedCartData = JSON.parse(storedCartData);

          if (!Array.isArray(parsedCartData)) {
            console.error(
              'Invalid cart data format - expected array, got:',
              typeof parsedCartData,
            );
            this.clearCorruptedCart();
            return;
          }

          const processedItems = [];
          for (const item of parsedCartData.filter((item) =>
            this.validateCartItem(item),
          )) {
            // Fix: Add default packaging for existing packages without packaging data
            if (
              !item.packaging ||
              (typeof item.packaging === 'object' &&
                Object.keys(item.packaging).length === 0)
            ) {
              item.packaging = {
                id: 'default-packaging',
                name: 'Standaard verpakking',
                price: 0,
                taxClass: 'standard',
                taxRate: 21,
                taxLabel: 'BTW',
                count: 1,
                quantity: 1,
              };
            }

            const processedItem = {
              ...item,
              quantity: this.validateQuantity(item.quantity),
              pricePerUnit: this.calculatePricePerUnit(item),
            };

            if (processedItem.personalization?.printing?.total_ex_vat) {
              processedItem.pricePerUnit =
                this.calculatePricePerUnit(processedItem);
            }

            if (processedItem.products) {
              processedItem.products = processedItem.products.map(
                (product) => ({
                  ...product,
                  quantity: product.quantity || product.count || 1,
                  count: product.quantity || product.count || 1,
                }),
              );
            }

            if (processedItem.foodDrinks) {
              processedItem.foodDrinks = processedItem.foodDrinks.map(
                (food) => ({
                  ...food,
                  quantity: food.quantity || food.count || 1,
                  count: food.quantity || food.count || 1,
                }),
              );
            }

            processedItems.push(processedItem);
          }

          this.cartItems = processedItems;

          this.$nextTick(async () => {
            await this.updateVatBreakdown();
          });

          if (this.cartItems.length > 0 && this.cartItems[0].priceFormat) {
            this.priceFormat = this.cartItems[0].priceFormat;
          }

          if (this.cartItems.length > 0) {
            this.updateVolumeDiscount().catch(() => {});
          }
        }
      } catch {
        this.clearCorruptedCart();
      }
    },

    validateCartItem(item) {
      if (!item || typeof item !== 'object') {
        return false;
      }

      if (!item.packageName || typeof item.packageName !== 'string') {
        return false;
      }

      const validatedQuantity = this.validateQuantity(item.quantity);
      if (validatedQuantity < 10) {
        console.error('Invalid cart, less then 10 items in the cart!');
        this.errors.value.general =
          'Invalid cart, less then 10 items in the cart!';
        return false;
      }

      if (!item.timestamp || isNaN(item.timestamp)) {
        return false;
      }

      return true;
    },

    validateQuantity(quantity) {
      const qty = parseInt(quantity);
      if (!isNaN(qty) && qty > 0 && qty <= 99999) {
        return qty;
      }
      return 1;
    },

    clearCorruptedCart() {
      localStorage.removeItem('composedPackages');
      this.cartItems = [];
      alert(
        'Er is een probleem opgetreden met je winkelmandje. Deze is leeggemaakt.',
      );
    },

    calculatePricePerUnit(item) {
      try {
        const baseItem = { ...item, quantity: 1, pricePerUnit: 0 };
        const baseVatBreakdown = calculateVatBreakdownByProduct([baseItem]);

        let printingCostPerUnit = 0;
        if (item.personalization?.printing?.per_package?.total_ex_vat) {
          printingCostPerUnit =
            item.personalization.printing.per_package.total_ex_vat;
        } else if (item.personalization?.printing?.total_ex_vat) {
          printingCostPerUnit =
            item.personalization.printing.total_ex_vat / (item.quantity || 1);
        }

        const basePrice = baseVatBreakdown.subtotalExVat;
        const totalPrice = basePrice + printingCostPerUnit;
        return Math.round(totalPrice * 100) / 100;
      } catch (error) {
        console.error('Error calculating price for item:', item, error);
        return 0;
      }
    },

    getVatRateLabel(rate) {
      if (Math.abs(rate - 0.09) < 0.001) return 'Laag';
      if (Math.abs(rate - 0) < 0.001) return 'Vrij';
      return 'Hoog';
    },

    async updatePackageQuantity(index, newQuantity) {
      if (index < 0 || index >= this.cartItems.length) {
        return;
      }

      if (newQuantity >= 10 && newQuantity <= 99999) {
        const updatedCartItems = [...this.cartItems];
        updatedCartItems[index] = {
          ...updatedCartItems[index],
          quantity: newQuantity,
        };

        const item = updatedCartItems[index];
        this.cartItems = updatedCartItems;

        if (this.getPersonalizedProductsCount(item) > 0) {
          try {
            await this.recalculatePrintingCosts(item);
          } catch (error) {
            console.error(
              'Error recalculating printing costs during quantity update:',
              error,
            );
          }
        }

        await this.updateVatBreakdown();
        await this.$nextTick();

        const updatedCartItemsFinal = [...this.cartItems];
        if (updatedCartItemsFinal[index]) {
          updatedCartItemsFinal[index].pricePerUnit =
            this.calculatePricePerUnit(updatedCartItemsFinal[index]);
          this.cartItems = updatedCartItemsFinal;
        }

        this.saveCart();
      }
    },

    async handleQuantityChanged(event) {
      // Quantity changed event handled - recalculate printing costs if needed
      const index = event.detail?.index;
      if (index !== undefined && index >= 0 && index < this.cartItems.length) {
        const item = this.cartItems[index];
        if (this.getPersonalizedProductsCount(item) > 0) {
          try {
            await this.recalculatePrintingCosts(item);
          } catch (error) {
            console.error(
              'Error recalculating printing costs during quantity change event:',
              error,
            );
          }
        }

        await this.updateVatBreakdown();
        item.pricePerUnit = this.calculatePricePerUnit(item);
      }
    },

    handleQuantityInvalid(event) {
      // Quantity invalid event handled
    },

    async handleQuantityCorrected(event) {
      // Quantity corrected event handled - recalculate printing costs if needed
      const index = event.detail?.index;
      if (index !== undefined && index >= 0 && index < this.cartItems.length) {
        const item = this.cartItems[index];
        if (this.getPersonalizedProductsCount(item) > 0) {
          try {
            await this.recalculatePrintingCosts(item);
          } catch (error) {
            console.error(
              'Error recalculating printing costs during quantity correction event:',
              error,
            );
          }
        }
      }
    },

    saveCart() {
      try {
        const validItems = this.cartItems.filter((item) =>
          this.validateCartItem(item),
        );

        if (validItems.length !== this.cartItems.length) {
          this.cartItems = validItems;
        }

        const itemsToSave = this.cartItems.map((item) => {
          if (item.personalization?.selectedCard) {
            const card = item.personalization.selectedCard;
            if (!card.originalPrice || card.originalPrice === card.price) {
              return item;
            }
          }
          return item;
        });

        localStorage.setItem('composedPackages', JSON.stringify(itemsToSave));

        if (this.cartItems.length === 0) {
          document.dispatchEvent(
            new CustomEvent('cart_cleared', {
              detail: { message: 'Winkelmandje is leeg' },
            }),
          );
        } else {
          document.dispatchEvent(
            new CustomEvent('cart_updated', {
              detail: { itemCount: this.cartItems.length },
            }),
          );
        }
      } catch (error) {
        console.error('Error saving cart:', error);
        alert(
          'Er is een probleem opgetreden bij het opslaan van je winkelmandje. Probeer het opnieuw.',
        );
      }
    },

    navigateToConfiguratorStep(packageIndex, step) {
      const item = this.cartItems[packageIndex];
      if (!item) {
        console.error('Invalid package index:', packageIndex);
        return;
      }

      const configData = {
        ...item,
        configVersion: '1.0',
      };

      sessionStorage.setItem('s7_current_config', JSON.stringify(configData));
      sessionStorage.setItem('s7_cart_item_key', packageIndex.toString());
      sessionStorage.setItem('s7_target_step', step);

      // Pass mailbox context for editing
      if (item.s7_is_mailbox_package) {
        sessionStorage.setItem('s7_is_mailbox_session', 'true');
        sessionStorage.setItem('s7_package_type', 'small');
      } else {
        sessionStorage.removeItem('s7_is_mailbox_session');
      }

      const flow = item.basePackage ? 'select' : 'compose';
      const url = `/${flow === 'compose' ? 'samenstellen' : 'pakket-kiezen'}`;
      window.location.href = url;
    },

    async removeItem(index) {
      if (index < 0 || index >= this.cartItems.length) {
        return;
      }

      const itemName =
        decodeHtml(this.cartItems[index].packageName) || 'dit pakket';

      if (confirm(`Weet je zeker dat je "${itemName}" wilt verwijderen?`)) {
        try {
          this.cartItems.splice(index, 1);
          this.saveCart();

          localStorage.removeItem('s7_configuration_draft');
          sessionStorage.removeItem('s7_current_config');
          sessionStorage.removeItem('s7_cart_item_key');
          sessionStorage.removeItem('s7_target_step');

          await this.updateVolumeDiscount();
          await this.updateVatBreakdown();

          document.dispatchEvent(
            new CustomEvent('cart_item_removed', {
              detail: {
                removedItem: itemName,
                remainingCount: this.cartItems.length,
              },
            }),
          );

          if (this.cartItems.length === 0) {
            this.storedVolumeDiscountPercent = 0;
            this.vatBreakdownData = {
              subtotalExVat: 0,
              subtotalAfterDiscount: 0,
              volumeDiscountPercent: 0,
              volumeDiscountAmount: 0,
              vatRates: new Map(),
              total: 0,
              totalInclVat: 0,
              totalVat: 0,
            };
            document.dispatchEvent(
              new CustomEvent('cart_cleared', {
                detail: { message: 'Winkelmandje is leeg' },
              }),
            );
          }
        } catch (error) {
          console.error('Error removing item:', error);
          alert(
            'Er is een probleem opgetreden bij het verwijderen van het pakket. Probeer het opnieuw.',
          );
        }
      }
    },

    getProductPersonalization(product, item) {
      if (!item.personalization?.productPersonalization || !product.id) {
        return null;
      }

      const personalization =
        item.personalization.productPersonalization[product.id];
      if (!personalization) {
        return null;
      }

      if (personalization.graveren) return 'Graveren';
      if (personalization.zeefdruk) return 'Zeefdruk';
      if (personalization.sticker) return 'Sticker';

      return null;
    },

    getPersonalizedProductsCount(personalizedItem) {
      if (!personalizedItem.personalization?.productPersonalization) return 0;

      return Object.values(
        personalizedItem.personalization.productPersonalization,
      ).filter(
        (personalization) =>
          personalization &&
          (personalization.sticker ||
            personalization.zeefdruk ||
            personalization.graveren),
      ).length;
    },

    getLogoFilename(item) {
      if (
        !item.personalization?.logoAttachments ||
        item.personalization.logoAttachments.length === 0
      ) {
        return null;
      }

      // Return the filename of the first logo
      return (
        item.personalization.logoAttachments[0].filename ||
        item.personalization.logoAttachments[0].name ||
        'Geüpload logo'
      );
    },

    async proceedToCheckout() {
      if (this.cartItems.length === 0) {
        alert('Je winkelmandje is leeg. Voeg eerst pakketten toe.');
        return;
      }

      // Force recalculation of prices before building payload
      await this.$nextTick();

      const invalidItems = this.cartItems.filter((item, index) => {
        let invalid = !item.packageName || !item.pricePerUnit;
        if (invalid) {
          console.error(
            'Pakket met missende informatie:',
            item,
            item.packageName,
            item.pricePerUnit,
            index,
          );
        }
        return invalid;
      });
      if (invalidItems.length > 0) {
        console.error('Pakketten missen informatie:', invalidItems);
        alert(
          'Sommige pakketten missen essentiële informatie. Probeer het opnieuw.',
        );
        return;
      }

      try {
        const checkoutPayload = {
          items: this.cartItemsWithPrices.map((item, index) => {
            const lineTotalExVat =
              Math.round((item.lineTotalExVat || 0) * 100) / 100;

            return {
              name: decodeHtml(item.packageName) || 'Onbekend pakket',
              packageName: decodeHtml(item.packageName) || 'Onbekend pakket',
              quantity: item.quantity || 1,
              basePackage: item.basePackage || null,
              isBasePackage: item.isBasePackage || false,
              pricePerUnitExVat: item.pricePerUnit || 0,
              packageType:
                item.packageType ||
                (item.s7_is_mailbox_package ? 'small' : 'large'),
              s7_is_mailbox_package: item.s7_is_mailbox_package || false,
              products: (item.products || [])
                .filter((product) => {
                  // Fix: Don't show products that are used as packaging
                  if (
                    (item.packaging && item.packaging.id === product.id) ||
                    (item.packagingProduct &&
                      item.packagingProduct.id === product.id)
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((product) => ({
                  id: product.id || `product_${Date.now()}`,
                  name: decodeHtml(product.name) || 'Onbekend product',
                  price: product.price || 0,
                  count: product.count || product.quantity || 1,
                  quantity: product.quantity || product.count || 1,
                  taxClass: product.taxClass,
                  taxRate: product.taxRate,
                  taxLabel: product.taxLabel,
                })),
              foodDrinks: (item.foodDrinks || []).map((food) => ({
                id: food.id || `food_${Date.now()}`,
                name: decodeHtml(food.name) || 'Onbekende drank/voeding',
                price: food.price || 0,
                count: food.count || food.quantity || 1,
                quantity: food.quantity || food.count || 1,
                taxClass: food.taxClass,
                taxRate: food.taxRate,
                taxLabel: food.taxLabel,
              })),
              packaging: (() => {
                // Ensure packaging data is properly formatted
                if (
                  item.packaging &&
                  typeof item.packaging === 'object' &&
                  Object.keys(item.packaging).length > 0
                ) {
                  return {
                    ...item.packaging,
                    taxClass: item.packaging.taxClass,
                    taxRate: item.packaging.taxRate,
                    taxLabel: item.packaging.taxLabel,
                  };
                }

                // Fix: Add default packaging for existing packages without packaging data
                if (
                  !item.packaging ||
                  (typeof item.packaging === 'object' &&
                    Object.keys(item.packaging).length === 0)
                ) {
                  return {
                    id: 'default-packaging',
                    name: 'Standaard verpakking',
                    price: 0,
                    taxClass: 'standard',
                    taxRate: 21,
                    taxLabel: 'BTW',
                    count: 1,
                    quantity: 1,
                  };
                }

                return null;
              })(),
              packagingMode: item.packagingMode || 'box',
              packagingProduct: item.packagingProduct || null,
              personalization: item.personalization || {},
              pricePerUnitExVat: item.pricePerUnit || 0,
              lineTotalExVat: lineTotalExVat,
              timestamp: new Date().toISOString(),
            };
          }),
          totals: {
            subtotalExVat: this.subtotalExVat || 0,
            vatRates: this.vatRatesArray || [],
            total: this.total || 0,
            volumeDiscountPercent: this.storedVolumeDiscountPercent || 0,
            currency: 'EUR',
            vatIncluded: false,
          },
          version: 'v1',
          source: 'cart',
          metadata: {
            cartItemCount: this.cartItems.length,
            generatedAt: new Date().toISOString(),
            userAgent: navigator.userAgent || 'Unknown',
            screenResolution: `${screen.width || 0}x${screen.height || 0}`,
            timezone:
              Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown',
          },
        };

        try {
          sessionStorage.setItem(
            's7_checkout_payload_v1',
            JSON.stringify(checkoutPayload),
          );
        } catch (storageError) {}

        if (
          this.$router &&
          this.$router.currentRoute.value.path !== '/afrekenen/'
        ) {
          this.$router.push('/afrekenen/').catch(() => {
            window.location.href = '/afrekenen/';
          });
        } else {
          window.location.href = '/afrekenen/';
        }
      } catch (error) {
        console.error('Error preparing checkout payload:', error);
        alert(
          'Er is een probleem opgetreden bij het voorbereiden van de checkout. Probeer het opnieuw.',
        );
      }
    },

    openProductModal(product, index, productIndex, type) {
      this.detailModalDetails = {
        product: product,
        count: product.count || 1,
        index: index,
        productIndex: productIndex,
        type: type,
      };
      this.showProductDetailModal = true;
    },

    checkUserLoggedIn() {
      this.isUserLoggedIn = document.body.classList.contains('logged-in');
    },

    initializeSaveCartTooltip() {
      if (this.$refs.saveCartBtn && !this.isUserLoggedIn) {
        this.saveCartTooltip = new Tooltip(this.$refs.saveCartBtn);
      }
    },

    destroySaveCartTooltip() {
      if (this.saveCartTooltip) {
        try {
          this.saveCartTooltip.dispose();
        } catch (error) {}
        this.saveCartTooltip = null;
      }

      document.querySelectorAll('.tooltip').forEach((tooltip) => {
        tooltip.remove();
      });
    },

    reinitializeSaveCartTooltip() {
      this.destroySaveCartTooltip();
      this.$nextTick(() => {
        this.initializeSaveCartTooltip();
      });
    },

    async saveCartManually() {
      this.destroySaveCartTooltip();

      if (this.cartItems.length === 0) {
        alert('Je winkelmandje is leeg');
        return;
      }

      if (!this.isUserLoggedIn) {
        window.location.href =
          '/mijn-account/?redirect_to=' +
          encodeURIComponent(window.location.pathname);
        return;
      }

      const name = prompt(
        'Geef je winkelmandje een naam:',
        `Winkelmandje ${new Date().toLocaleDateString('nl-NL')}`,
      );

      if (!name) return;

      this.isSaving = true;

      try {
        const response = await fetch(
          '/wp-admin/admin-ajax.php?action=s7_cart_save',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              cart_data: this.cartItems,
              name: name,
            }),
          },
        );

        const data = await response.json();

        if (data.success) {
          this.cartSavedRecently = true;
          this.showSavedNotice = true;
          setTimeout(() => {
            this.showSavedNotice = false;
          }, 5000);
        } else {
          alert(data.data?.message || 'Fout bij opslaan winkelmandje');
        }
      } catch (error) {
        console.error('Error saving cart:', error);
        alert('Er is een fout opgetreden bij het opslaan');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
